import * as React from 'react';
import { observer } from 'mobx-react-lite';
import {
    Modal,
    Form,
    Row,
    Col,
    Select,
    Radio,
    Input,
    InputNumber,
    Button,
    Table,
    Tooltip,
    message,
    DatePicker
} from 'antd';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import { ColumnProps } from 'antd/lib/table';
import { useTableScrollHeight } from '../../hooks';
import { BusinessDto, BusinessSearchCondition, IBusinessSearchStoreBase, BusinessRequestFilter } from '../../types';
import { TypeUtils } from '../../../common/misc';
import { Utils } from '../../misc';
import './BusinessSearchModalBase.less';

export type BusinessSearchModalFormValues = {
    businessTitle?: string;
    typeOfBusiness?: string;
    businessPartners: string[];
    searchCondition: BusinessSearchCondition;
    insuredPeriodStart?: unknown;
    insuredPeriodEnd?: unknown;
    underwritingYear?: number;
};

interface InitialFormValues {
    underwritingYear?: unknown;
    insuredPeriodStart?: unknown;
    insuredPeriodEnd?: unknown;
}

interface Props {
    formId: string;
    businessSearchStore: IBusinessSearchStoreBase;
    initialFormValues: InitialFormValues;
    onClose: () => void;
    onBusinessClick: (business: BusinessDto) => void;
}

const searchConditions: Record<BusinessSearchCondition, BusinessSearchCondition> = { And: 'And', Or: 'Or' };

const searchConditionOptions = Object.values(searchConditions).map(condition => ({
    label: condition,
    value: condition
}));

const renderCell = (value: string | number) => <div className="table-cell-content">{value}</div>;

const validateBusinessSearchFilter = (filter: BusinessRequestFilter): boolean => {
    return [
        filter.underwritingYear,
        filter.insuredPeriodStart,
        filter.insuredPeriodEnd,
        filter.businessPartners.length,
        filter.businessTitle,
        filter.typeOfBusiness
    ].some(Boolean);
};

const BusinessSearchModalBase: React.FC<Props> = (props: Props) => {
    const { formId, businessSearchStore, initialFormValues, onClose, onBusinessClick } = props;

    const [form] = Form.useForm<BusinessSearchModalFormValues>();

    const { scrollHeight } = useTableScrollHeight(700);

    React.useEffect(() => {
        if (!businessSearchStore.modalVisible) {
            return;
        }

        const { underwritingYear, insuredPeriodStart, insuredPeriodEnd } = initialFormValues;

        form.setFieldsValue({
            underwritingYear: TypeUtils.isNumber(underwritingYear) ? underwritingYear : undefined,
            insuredPeriodStart: Utils.convertDateValueToPickerValue(insuredPeriodStart),
            insuredPeriodEnd: Utils.convertDateValueToPickerValue(insuredPeriodEnd)
        });
    }, [form, initialFormValues, businessSearchStore.modalVisible]);

    const handleSubmit = () => {
        const insuredPeriodStart = form.getFieldValue('insuredPeriodStart');
        const insuredPeriodEnd = form.getFieldValue('insuredPeriodEnd');
        const businessTitle = (form.getFieldValue('businessTitle') ?? '').trim();

        const filter = {
            ...form.getFieldsValue(),
            businessTitle: businessTitle.length > 0 ? businessTitle : undefined,
            insuredPeriodStart:
                Utils.convertDateValueToString(insuredPeriodStart, { applyStartOfDay: true }) ?? undefined,
            insuredPeriodEnd: Utils.convertDateValueToString(insuredPeriodEnd, { applyEndOfDay: true }) ?? undefined
        };

        if (!validateBusinessSearchFilter(filter)) {
            message.warning('You need to select at least one parameter to perform a search.');
            return;
        }

        businessSearchStore.updatePaging(0, businessSearchStore.pageSize);
        businessSearchStore.getBusinesses(filter);
    };

    const columns: ColumnProps<BusinessDto>[] = [
        {
            title: 'Business ID',
            dataIndex: 'identifier',
            key: 'identifier',
            render: (value: string) => renderCell(value),
            onCell: () => ({
                style: { minWidth: 80 }
            })
        },
        {
            title: 'Business Title',
            dataIndex: 'title',
            key: 'title',
            render: (value: string) => <Tooltip title={value}>{renderCell(value)}</Tooltip>,
            onCell: () => ({
                style: { maxWidth: 250 }
            })
        },
        {
            title: 'Underwriting Year',
            dataIndex: 'underwritingYear',
            key: 'underwritingYear',
            render: (value: number) => renderCell(value),
            onCell: () => ({
                style: { minWidth: 100 }
            })
        },
        {
            title: 'Cover',
            dataIndex: 'cover',
            key: 'cover',
            render: (value: number) => renderCell(Utils.formatAmount(value)),
            onCell: () => ({
                style: { minWidth: 80 }
            })
        },
        {
            title: 'Excess',
            dataIndex: 'excess',
            key: 'excess',
            render: (value: number) => renderCell(Utils.formatAmount(value)),
            onCell: () => ({
                style: { minWidth: 80 }
            })
        },
        {
            title: 'Liability',
            dataIndex: 'liability',
            key: 'liability',
            render: (value: number) => renderCell(Utils.formatAmount(value)),
            onCell: () => ({
                style: { minWidth: 80 }
            })
        }
    ];

    const pagination: TablePaginationConfig = {
        current: businessSearchStore.page + 1,
        pageSize: businessSearchStore.pageSize,
        hideOnSinglePage: true,
        pageSizeOptions: ['10', '25', '50'],
        onChange: (page, pageSize) => {
            businessSearchStore.updatePaging(page - 1, pageSize ?? 10);
        }
    };

    const handleCancel = () => {
        onClose();
        form.resetFields();
    };

    return (
        <Modal
            className="business-search-modal"
            title="Business Search"
            visible={businessSearchStore.modalVisible}
            maskClosable={false}
            width={1000}
            footer={null}
            destroyOnClose
            forceRender
            centered
            onCancel={handleCancel}
        >
            <Form id={formId} layout="vertical" form={form} preserve={false}>
                <Row gutter={20}>
                    <Col span={24}>
                        <Form.Item
                            name="businessTitle"
                            label="Business Title"
                            rules={[
                                {
                                    max: 100,
                                    message: 'Business title cannot be longer than 100 characters'
                                }
                            ]}
                        >
                            <Input allowClear />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="businessPartners" label="Business Partner" initialValue={[]}>
                            <Select
                                showSearch
                                allowClear
                                mode="multiple"
                                dropdownClassName="custom-select-dropdown"
                                options={businessSearchStore.businessPartnersOptions}
                                loading={businessSearchStore.businessPartnersLoading}
                                autoClearSearchValue={false}
                                filterOption={(value, option) =>
                                    option?.title && option.title.toLowerCase().includes(value.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="searchCondition"
                            label="Partner Filtering Operator"
                            initialValue={searchConditions.And}
                        >
                            <Radio.Group options={searchConditionOptions} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="insuredPeriodStart" label="Insured Period Start Date">
                            <DatePicker placeholder="Select date" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item name="insuredPeriodEnd" label="Insured Period End Date">
                            <DatePicker placeholder="Select date" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={20}>
                    <Col span={12}>
                        <Form.Item name="underwritingYear" label="Underwriting Year">
                            <InputNumber />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item name="typeOfBusiness" label="Type of Business">
                            <Select
                                allowClear
                                dropdownClassName="custom-select-dropdown"
                                options={businessSearchStore.typeOfBusinessOptions}
                                filterOption={(value, option) =>
                                    option?.title && option.title.toLowerCase().includes(value.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={20}>
                    <Col className="find-now-column" span={24}>
                        <Button type="primary" onClick={handleSubmit} disabled={businessSearchStore.businessesLoading}>
                            Find Now
                        </Button>
                    </Col>
                </Row>
            </Form>

            <Table
                className="business-search-table"
                dataSource={businessSearchStore.businesses}
                loading={businessSearchStore.businessesLoading}
                columns={columns}
                scroll={{ y: scrollHeight }}
                pagination={pagination}
                tableLayout="auto"
                rowKey={(business: BusinessDto) => businessSearchStore.businesses.indexOf(business).toString()}
                onRow={business => ({
                    onDoubleClick: () => onBusinessClick(business)
                })}
            />
        </Modal>
    );
};

export default observer(BusinessSearchModalBase);
